import React from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '../components/LanguageSelector';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    AppBar,
    Box,
    CssBaseline,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Toolbar,
    Typography,
    Divider,
} from '@mui/material';
import {
    Menu as MenuIcon,
    Logout as LogoutIcon,
    Person as PersonIcon,
    Quiz as QuizIcon,
    People as PeopleIcon,
    Assessment as AssessmentIcon,
    Category as CategoryIcon,
    Login as LoginIcon,
    PersonAdd as PersonAddIcon,
    Dashboard as DashboardIcon,
    Settings as SettingsIcon,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';

const drawerWidth = 240;

interface MainLayoutProps {
    children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
    const [mobileOpen, setMobileOpen] = React.useState(false);
    const { user, logout, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const { t } = useTranslation();
    const isAdmin = user?.role === 'admin';
    const isContentCreator = user?.role === 'content_creator';

    const navigationItems = !isAuthenticated ? [
        ...(location.pathname === '/quizzes' ? [] : [
            { text: t('quiz.availableQuizzes'), icon: <QuizIcon />, path: '/' },
        ]),
        { text: t('common.register'), icon: <PersonAddIcon />, path: '/register' },
        { text: t('common.login'), icon: <LoginIcon />, path: '/login' },
    ] : [
        ...(isAdmin ? [
            { text: t('common.dashboard'), icon: <DashboardIcon />, path: '/admin' },
            { text: t('admin.contentCreators.title'), icon: <PeopleIcon />, path: '/admin/content-creators' },
            { text: t('admin.systemSettings.title'), icon: <SettingsIcon />, path: '/admin/system-settings' },
        ] : []),
        ...(isContentCreator ? [
            { text: t('common.dashboard'), icon: <DashboardIcon />, path: '/content-creator' },
            { text: t('contentCreator.themes'), icon: <CategoryIcon />, path: '/content-creator/themes' },
            { text: t('contentCreator.questionPapers'), icon: <QuizIcon />, path: '/content-creator/papers' },
        ] : []),
        ...((!isAdmin && !isContentCreator) ? [
            { text: t('common.dashboard'), icon: <DashboardIcon />, path: '/student' },
            { text: t('quiz.availableQuizzes'), icon: <QuizIcon />, path: '/student/quizzes' },
            { text: t('student.myAttempts'), icon: <AssessmentIcon />, path: '/student/attempts' },
        ] : []),
        { text: t('common.profileLink'), icon: <PersonIcon />, path: '/profile' },
    ];

    const drawer = (
        <div>
            <Toolbar>
                <Typography variant="h6" noWrap component="div" sx={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
                    {t('common.brandName', 'SDG')}
                </Typography>
            </Toolbar>
            <Divider />
            <List>
                {navigationItems.map((item) => (
                    <ListItem key={item.text} disablePadding>
                        <ListItemButton
                            selected={location.pathname === item.path}
                            onClick={() => navigate(item.path)}
                        >
                            <ListItemIcon>{item.icon}</ListItemIcon>
                            <ListItemText primary={item.text} />
                        </ListItemButton>
                    </ListItem>
                ))}
                {isAuthenticated && (
                    <ListItem disablePadding>
                        <ListItemButton onClick={logout}>
                            <ListItemIcon><LogoutIcon /></ListItemIcon>
                            <ListItemText primary={t('common.logout')} />
                        </ListItemButton>
                    </ListItem>
                )}
            </List>
        </div>
    );

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <AppBar
                position="fixed"
                sx={{
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    ml: { sm: `${drawerWidth}px` },
                }}
            >
                <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton
                            color="inherit"
                            aria-label={t('common.openDrawer')}
                            edge="start"
                            onClick={handleDrawerToggle}
                            sx={{ mr: 2, display: { sm: 'none' } }}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                            {t('common.brandName', 'SDG')}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <LanguageSelector />
                        {isAuthenticated && (
                            <Typography variant="subtitle1">
                                {user?.first_name} {user?.last_name}
                            </Typography>
                        )}
                    </Box>
                </Toolbar>
            </AppBar>
            <Box
                component="nav"
                sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
            >
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true, // Better open performance on mobile.
                    }}
                    sx={{
                        display: { xs: 'block', sm: 'none' },
                        '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                    }}
                >
                    {drawer}
                </Drawer>
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', sm: 'block' },
                        '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                    }}
                    open
                >
                    {drawer}
                </Drawer>
            </Box>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    mt: 8,
                }}
            >
                {children}
            </Box>
        </Box>
    );
};

export default MainLayout;
